import React, { useMemo, useState, useEffect } from "react";
import { View } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { OfferCardColors } from "@/constants/Colors";
import { OfferCardHeader } from "./offerCard/OfferCardHeader";
import { OfferCardActions } from "./offerCard/OfferCardActions";
import { OfferCardLayoutDataFactory } from "./offerCard/layoutDataFactory";
import { generateOfferTitle, isValidOfferType } from "./offerCard/utils";
import { OfferCardProps } from "./offerCard/types";
import { generateClaimPDF } from "@/services/claims";
import { showError } from "@/utils/toast";
import { safeOpenURL } from "@/utils/linking";
import FBTrackingService from "@/services/fbTrackingService";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";

export function OfferCard({
  data,
  onHide,
  onClaim,
  onStartMonitoring,
}: OfferCardProps) {
  const [isDownloading, setIsDownloading] = useState(false);

  // Handle download with loading state
  const handleDownload = async () => {
    if (isDownloading) return; // Prevent multiple clicks

    setIsDownloading(true);
    try {
      const response = await generateClaimPDF(data.id || "");

      if (response.status === "success" && response.url) {
        const opened = await safeOpenURL(response.url);
        if (!opened) {
          showError("Unable to open PDF. Please try again.");
        }
      } else {
        showError(response.message || "Failed to generate PDF");
      }
    } catch (error) {
      console.error("Download error:", error);
      showError("Failed to download PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const layoutData = useMemo(() => {
    return OfferCardLayoutDataFactory.createLayoutData(
      data,
      onClaim,
      onStartMonitoring,
      handleDownload,
      isDownloading
    );
  }, [data, onClaim, onStartMonitoring, isDownloading]);

  const title = useMemo(
    () => layoutData.title || generateOfferTitle(data),
    [data, layoutData.title]
  );

  // Track when different offer types are shown
  useEffect(() => {
    if (data.type === "unclaimedMoney" || data.type === "unclaimedProperty") {
      FBTrackingService.trackEvent(TRACKING_EVENTS.UNCLAIMED_MONEY_SHOWN, {
        eventData: {
          type: data.type,
          amount: "amount" in data ? data.amount : undefined,
          data: data,
        },
      });
    } else if (data.type === "classAction") {
      FBTrackingService.trackEvent(TRACKING_EVENTS.CLASS_ACTION_SHOWN, {
        eventData: {
          id: data.id,
          title: data.title,
          payout: data.payout,
          suitId: data.suitId,
        },
      });
    } else if (data.type === "bank") {
      FBTrackingService.trackEvent(TRACKING_EVENTS.BANK_OFFER_SHOWN, {
        eventData: {
          id: data.id,
          title: data.title,
          signUpBonus: data.signUpBonus,
          monthlyFee: data.monthlyFee,
        },
      });
    }
  }, [data]);

  if (!isValidOfferType(data)) {
    return null;
  }

  return (
    <View style={[styles.card, layoutData.disabled && styles.disabled]}>
      <OfferCardHeader
        data={data}
        title={title}
        tags={layoutData.tags}
        amountText={layoutData.amountText}
      />
      <OfferCardActions
        onHide={onHide}
        actionTitle={layoutData.actionTitle}
        actionIcon={layoutData.actionIcon}
        onActionPress={layoutData.onPress}
        notes={layoutData.notes}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: OfferCardColors.card.background,
    borderRadius: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: "#EBEBEB",
    overflow: "hidden",
  },
  disabled: {
    opacity: 0.6,
  },
});
